import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../auth/AuthContext'
import { safeNextPath } from '../../../auth/ProtectedRoute'
import { isEmail } from '../../../utils/inputs'

export const REGISTER_PATH = '/registro'

export type LoginView = 'login' | 'recuperar'

const LOGIN_ERROR = 'Correo o contraseña incorrectos. Revisa tus datos e inténtalo de nuevo.'
const RESET_ERROR = 'No pudimos enviar el enlace. Inténtalo de nuevo.'

const errorMessage = (error: unknown, fallback: string) => (error instanceof Error && error.message) || fallback

export const useLoginFormModel = () => {
    const { login, restorePassword } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const nextUrl = new URLSearchParams(location.search).get('url')

    const [view, setView] = useState<LoginView>('login')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [remember, setRemember] = useState(true)
    const [tried, setTried] = useState(false)
    const [loading, setLoading] = useState(false)
    const [authError, setAuthError] = useState('')
    const [recSent, setRecSent] = useState(false)
    const [resent, setResent] = useState(false)

    const emailOk = isEmail(email)

    const go = (next: LoginView) => {
        setView(next)
        setTried(false)
        setAuthError('')
        setRecSent(false)
        setResent(false)
    }

    const submitLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!emailOk || !password) {
            setTried(true)
            return
        }
        setLoading(true)
        setAuthError('')
        try {
            await login({ usuario: email, password, remember })
            navigate(safeNextPath(nextUrl), { replace: true })
        } catch (error) {
            setAuthError(errorMessage(error, LOGIN_ERROR))
        } finally {
            setLoading(false)
        }
    }

    const submitReset = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!emailOk) {
            setTried(true)
            return
        }
        setLoading(true)
        try {
            await restorePassword(email)
            setRecSent(true)
            setTried(false)
        } catch (error) {
            setAuthError(errorMessage(error, RESET_ERROR))
        } finally {
            setLoading(false)
        }
    }

    const resend = async () => {
        await restorePassword(email)
        setResent(true)
    }

    return {
        view,
        email,
        password,
        showPassword,
        remember,
        loading,
        authError,
        recSent,
        resent,
        emailErr: tried && !emailOk,
        passErr: tried && !password,
        setEmail: (value: string) => { setEmail(value.trim()); setAuthError('') },
        setPassword: (value: string) => { setPassword(value); setAuthError('') },
        toggleShowPassword: () => setShowPassword((value) => !value),
        toggleRemember: () => setRemember((value) => !value),
        goLogin: () => go('login'),
        goRecover: () => go('recuperar'),
        submitLogin,
        submitReset,
        resend,
    }
}
